import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal } from "../Motion";
import { money } from "../../lib/api";

export function CatalogPreview({ releases }) {
  const navigate = useNavigate();
  if (!releases?.length) return null;
  return (
    <section className="relative py-24 md:py-36 bg-[#050505]" data-testid="catalog-preview">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-12">FEATURED MUSIC</p>
        </Reveal>
        {releases.slice(0, 3).map((r, i) => (
          <Reveal key={r.slug} delay={i * 0.08}>
            <button
              onClick={() => navigate(`/music/${r.slug}`)}
              data-testid={`catalog-preview-${r.slug}`}
              className="group w-full flex items-center justify-between py-8 border-t border-white/10 text-left hover:px-3 transition-all duration-300"
            >
              <div>
                <span className="font-serif text-3xl md:text-5xl font-light text-white group-hover:text-gold transition-colors duration-300">
                  {r.titleParts ? `${r.titleParts.chapter} — ${r.titleParts.name}` : r.title}
                </span>
                <p className="font-mono text-[10px] tracking-[0.3em] text-white/40 mt-3">
                  {r.releaseType.toUpperCase()} · {r.year} · {r.status}
                </p>
              </div>
              <span className="font-mono text-gold text-lg">→</span>
            </button>
          </Reveal>
        ))}
        <Reveal delay={0.2}>
          <div className="border-t border-white/10 pt-8">
            <Link to="/music" data-testid="catalog-preview-all" className="font-mono text-[11px] tracking-[0.25em] text-white/50 hover:text-gold transition-colors">
              VIEW THE FULL CATALOG →
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function ArtistStatement({ heading, copy }) {
  if (!heading && !copy) return null;
  return (
    <section className="relative py-32 md:py-44 bg-[#070707]" data-testid="artist-statement">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="max-w-4xl">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-10">THE ARTIST</p>
            {heading && (
              <h2 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.95] text-white">
                {heading}
              </h2>
            )}
          </Reveal>
          <Reveal delay={0.15}>
            {copy && (
              <p className="mt-10 text-lg leading-relaxed text-white/55 max-w-xl">
                {copy}
              </p>
            )}
            <Link to="/artist" data-testid="artist-statement-link" className="inline-block mt-10 font-mono text-[11px] tracking-[0.25em] text-white/50 hover:text-gold transition-colors">
              EXPLORE THE ARTIST →
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function SelectedVisuals({ visuals, featuredId }) {
  const navigate = useNavigate();
  const featured = visuals?.find((v) => v.id === featuredId) || visuals?.find((v) => v.featured) || visuals?.[0];
  if (!featured) return null;
  return (
    <section className="relative py-24 md:py-36 bg-[#050505]" data-testid="selected-visuals">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-12">SELECTED VISUALS</p>
        </Reveal>
        <Reveal y={60}>
          <button
            onClick={() => navigate("/visuals")}
            data-testid="selected-visuals-featured"
            className="group relative w-full overflow-hidden aspect-[21/9] block"
          >
            <motion.img
              src={featured.thumbnail}
              alt={featured.title}
              className="w-full h-full object-cover"
              whileHover={{ scale: 1.04 }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-8 md:p-12 text-left">
              <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-3">{featured.type.toUpperCase()}</p>
              <p className="font-serif text-3xl md:text-5xl font-light text-white group-hover:text-gold transition-colors duration-300">
                {featured.title}
              </p>
            </div>
          </button>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/visuals" data-testid="selected-visuals-all" className="inline-block mt-10 font-mono text-[11px] tracking-[0.25em] text-white/50 hover:text-gold transition-colors">
            ENTER THE ARCHIVE →
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export function StorePreview({ products, pickKeys }) {
  const navigate = useNavigate();
  if (!products?.length) return null;
  const picks = pickKeys?.length
    ? pickKeys.map((k) => products.find((p) => p.lookupKey === k)).filter(Boolean)
    : products.slice(0, 3);
  return (
    <section className="relative py-24 md:py-36 bg-[#070707]" data-testid="store-preview">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-12">THE STORE</p>
        </Reveal>
        {picks.map((p, i) => (
          <Reveal key={p.lookupKey} delay={i * 0.08}>
            <button
              onClick={() => navigate(`/store/${p.lookupKey}`)}
              data-testid={`store-preview-${p.lookupKey}`}
              className="group w-full grid grid-cols-12 items-center gap-4 py-7 border-t border-white/10 text-left hover:px-3 transition-all duration-300"
            >
              <div className="col-span-8 md:col-span-6">
                <span className="font-serif text-2xl md:text-4xl font-light text-white group-hover:text-gold transition-colors duration-300">{p.name}</span>
                <p className="font-mono text-[10px] tracking-[0.25em] text-white/40 mt-2">{p.edition.toUpperCase()} · {p.format.toUpperCase()}</p>
              </div>
              <div className="hidden md:block md:col-span-4 font-mono text-[10px] tracking-[0.25em] text-white/40">
                {p.subtitle.toUpperCase()}
              </div>
              <div className="col-span-4 md:col-span-2 text-right font-mono text-sm text-gold">{money(p.price, p.currency)}</div>
            </button>
          </Reveal>
        ))}
        <Reveal delay={0.2}>
          <div className="border-t border-white/10 pt-8">
            <Link to="/store" data-testid="store-preview-all" className="font-mono text-[11px] tracking-[0.25em] text-white/50 hover:text-gold transition-colors">
              VISIT THE STORE →
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function FinalStatement({ statement, cta }) {
  if (!statement) return null;
  const text = statement;
  return (
    <section className="relative py-36 md:py-52 bg-[#050505] overflow-hidden" data-testid="final-statement">
      <div className="mm-vignette" />
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 text-center">
        <Reveal>
          <p className="font-serif italic text-4xl sm:text-5xl lg:text-7xl font-light leading-[1.05] text-white/90">
            {text}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            to={cta?.target || "/join"}
            data-testid="final-statement-join"
            className="inline-block mt-14 border border-gold/60 text-gold font-mono text-[11px] tracking-[0.3em] px-10 py-4 hover:bg-gold hover:text-black transition-colors duration-300"
          >
            {cta?.label || "ENTER THE INNER CIRCLE"}
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
